import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

import { api } from '../../../../lib/api';
import useForm from '../../../../lib/hooks/useForm';
import { useNotification } from '../../../../shared/Notification/useNotification';

const mutationFn = values => {
  const { uuid, slug } = values;

  return api.patch(`/api/client_admin/campaigns/${uuid}/update_slug`, {
    campaign: {
      slug,
    },
  });
};

export const useUrlForm = ({ id, slug }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('campaigns');
  const initialValues = {
    uuid: id,
    slug: slug || '',
  };

  const form = useForm({
    initialValues,
    enableReinitialize: true,
  });

  const { mutate } = useMutation(mutationFn, {
    onSuccess: () => {
      setIsEditing(false);
      showSuccess({ content: t('urlInput.highlightUrlUpdated') });
    },
    onError: () => {
      form.setFieldError('slug', 'This URL has been used before.');
    },
  });

  const debouncedShowError = useMemo(
    () =>
      debounce(message => {
        showError({ content: message });
      }, 500),
    [showError],
  );

  const handleSubmit = () => {
    if (form.values.slug.length <= 0) {
      form.setFieldError('slug', 'The URL cannot be empty.');
      return;
    }

    if (form.dirty) {
      mutate(form.values);
    } else {
      setIsEditing(false);
    }
  };

  const handleSlugChange = value => {
    // Only allow letters, numbers, underscores, and dashes
    const replaceSpaces = value.replace(/\s/g, '-');
    const sanitizedValue = replaceSpaces.replace(/[^a-zA-Z0-9_-]/g, '');

    // Check if special characters were removed
    if (replaceSpaces !== sanitizedValue) {
      debouncedShowError(t('urlInput.specialCharactersError'));
    }

    form.setFieldValue('slug', sanitizedValue);
  };

  return {
    ...form,
    handleSubmit,
    handleSlugChange,
    isEditing,
    setIsEditing,
  };
};
